import { PortfolioService } from '@services/portfolio/PortfolioService';
import { BrandsService } from '@services/shared/BrandsService';
import { theme } from '@theme';
import { formatDate } from '@utils/DateUtils';
import { formatStringAsPerLocale, hasRole } from '@utils/HelperFunctions';
import { Dispatch, FC, SetStateAction, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Column } from 'react-table';
import styled from 'styled-components';
import FooterLoader from '@components/shared/loader/footerLoader/FooterLoader';
import { DealType } from '@enums/DealType';
import { SortDirection } from '@enums/SortDirection';
import {
  useDealType,
  useFilterBox,
  usePagination,
  usePrevious,
  useSort,
} from '@hooks/index';
import { useCheckDevice } from '@hooks/UseCheckDevice';
import { usePortfolioFilter } from '@hooks/UsePortfolioFilter';
import { AppState } from '@store/AppState';
import {
  resetAllAiMessages,
  setDealsheetPagination,
  setSelectedVehicleId,
} from '@store/dealsheet/DealsheetActions';
import {
  setIsPortfolioFilterReset,
  setIsPortfolioFiltersChanged,
  setIsPortfolioLoading,
  setIsPortfolioResetPaginator,
  setPortfolioDataTotalCount,
  setPortfolioNotesCountMap,
  setPortfolioNotesModalOpen,
  setPortfolioOptions,
  setPortfolioRequestResource,
  setPortfolioShouldRefresh,
  setPortfolioShouldRefreshDealCount,
  setPortfolioStoreId,
} from '@store/portfolio/PortfolioActions';
import { PortfolioState } from '@store/portfolio/PortfolioReducer';
import { CurrentUser } from '@appTypes/CurrentUser';
import { DealType as DealTypeExtended } from '@appTypes/DealType';
import { FilterItemResource } from '@appTypes/FilterTypes';
import { NotesCountMap } from '@appTypes/Note';
import { PortfolioFilterResource } from '@appTypes/PortfolioFilterResource';
import { PortfolioResource } from '@appTypes/PortfolioResource';
import { SortType } from '@appTypes/SortType';
import PrivateIncentiveModal from '../../shared/essential-ui/modals/privateIncentive-modal/PrivateIncentiveModal';
import PaginationContainer from '../../shared/pagination-options/PaginationContainer';
import ReactTableWrapper from '../../shared/react-table-wrapper/ReactTableWrapper';
import SearchBox from '../../shared/search-box/SearchBox';
import { Typography } from '../../style';
import PortfolioDropdown from '../header/portfolio-dropdown/PortfolioDropdown';
import PortfolioNotesModal from '../notes-modal/PortfolioNoteModal';
import {
  allDealsColumns,
  cashDealsColumns,
  financeDealsColumns,
  leaseDealsColumns,
  portfolioBaseColumns,
} from './columns/column-types/index';
import { PortfolioHeaderResultsContainer } from './components/PortfolioHeaderResultsContainer';
import { PortfolioSearchContainer } from './components/PortfolioSearchContainer';
import { PortfolioSecondaryHeaderLeftContainer } from './components/PortfolioSecondaryHeaderLeftContainer';
import { PortfolioTable } from './components/PortfolioTable';
import PortfolioNoResultFound from './table-header/portfolio-no-result-found/PortfolioNoResultFound';
import PortfolioTryAgain from './table-header/portfolio-try-again/PortfolioTryAgain';
import { DealerState } from '@appTypes/DealerState';

type PortfolioTableSectionProps = {
  dealType: string;
  storeId: string;
  toggleAllRowsValue?: boolean;
  refreshCount?: number;
  filteredRecordsCount?: number;
  isBulkOperation: boolean;
  setSelectedRows?: Dispatch<SetStateAction<{}>>;
  setIsAllRowsSelected?: Dispatch<SetStateAction<boolean>>;
  setFilteredRecordsCount?: Dispatch<SetStateAction<number>>;
  onPortfolioSelection: (dealType: string) => void;
  openDealSheet: (vin: string, dealerId: string, dealType: string) => void;
};

const PortfolioTableSection: FC<PortfolioTableSectionProps> = ({
  dealType,
  storeId,
  toggleAllRowsValue,
  refreshCount,
  filteredRecordsCount,
  isBulkOperation,
  setSelectedRows,
  setIsAllRowsSelected,
  setFilteredRecordsCount,
  onPortfolioSelection,
  openDealSheet,
}) => {
  const [portfolioData, setPortfolioData] = useState<PortfolioResource[]>([]);
  const [shouldRefreshCount, setShouldRefreshCount] = useState<number>(0);
  const initialRender = useRef<boolean>(true);
  const mounted = useRef<boolean>(true);
  const [isNoResult, setIsNoResult] = useState<boolean>(false);
  const [isTryAgain, setIsTryAgain] = useState<boolean>(false);
  const [fetchingScrollData, setFetchingScrollData] = useState<boolean>(false);
  const [searchBoxOpen, setSearchBoxOpen] = useState<boolean>(false);
  const tableRef = useRef(null);

  const previousDealType = usePrevious<string>(dealType);
  const {
    getPortfolioRequestResource,
    updateWithMasterFilter,
    getPortfolioView,
    resetFilters,
    createInsightsFilter,
  } = usePortfolioFilter();
  const currentView = getPortfolioView(dealType);
  const initialSort: SortType =
    currentView.portfolioRequest === null
      ? { sortDirection: SortDirection.Descending, accessor: 'bps' }
      : {
        sortDirection: currentView.portfolioRequest.sortDirection as SortDirection,
        accessor: currentView.portfolioRequest.sort,
      };
  const { sort, onSortChange } = useSort(initialSort);
  const { setCurrentPage, setPageSize, startIndex, endIndex, currentPage, totalPages, pageSize } =
    usePagination(
      filteredRecordsCount,
      currentView?.portfolioRequest?.currentPage,
      currentView?.portfolioRequest?.recordsPerPage,
    );
  const [currentSortedColumn, setCurrentSortedColumn] = useState<string>(
    currentView.portfolioRequest === null ? 'bps' : currentView.portfolioRequest.sort,
  );
  const { searchText, previousSearchText, setSearchText } = useFilterBox();
  const { getDealTypeNumber, getDealTypeLabel } = useDealType();
  const { isPrivateIncentiveModalOpen, privateIncentiveModalResource } = useSelector<
    AppState,
    PortfolioState
  >((state) => state.portfolio);
  const { isMobileDevice, isTabletDevice } = useCheckDevice();
  const {
    isLoading,
    currentStoreId,
    portfolioOptions,
    isPortfolioFilterReset,
    openFilterCount,
    isNotesModalOpen,
    notesModalResource,
    isPortfolioResetPaginator,
  } = useSelector<AppState, PortfolioState>((portfolioState) => portfolioState.portfolio);
  const { permissions } = useSelector<AppState, CurrentUser>((state) => state.currentUser);
  const dispatch = useDispatch();

  const options: FilterItemResource[] = [
    { key: DealType.AllDeals, label: DealType.AllDeals },
    { key: DealType.Lease, label: DealType.Lease },
    { key: DealType.Finance, label: DealType.Finance },
    { key: DealType.Cash, label: DealType.Cash },
  ];

  const getPortFolioColumns = (dealType: string): Column<PortfolioResource>[] => {
    switch (dealType) {
      case 'all':
        return allDealsColumns();

      case DealType.Finance.toLowerCase():
        return isMobileDevice
          ? portfolioBaseColumns()
          : [...portfolioBaseColumns(), ...financeDealsColumns];

      case DealType.Lease.toLowerCase():
        return isMobileDevice
          ? portfolioBaseColumns()
          : [...portfolioBaseColumns(), ...leaseDealsColumns];

      case DealType.Cash.toLowerCase():
        return isMobileDevice
          ? portfolioBaseColumns()
          : [...portfolioBaseColumns(), ...cashDealsColumns];

      default:
        return leaseDealsColumns;
    }
  };

  const reset = (): void => {
    resetFilters(dealType);
    setSearchText('');
    setIsNoResult(false);
  };

  const tryAgain = (): void => {
    setIsTryAgain(false);
    loadPortfolioData();
  };

  const loadDealCounts = (): void => {
    PortfolioService.fetchDealCounts().then((result) => {
      const updatedOptions = options.map((item) => {
        if (item.key === DealType.AllDeals) {
          const count = result.find((x) => x.label === '7')?.count;
          item.count = count;
        } else {
          const count = result.find(
            (x) => parseInt(x.label) === getDealTypeNumber(item.key),
          )?.count;
          item.count = count;
        }
        return item;
      });

      dispatch(setPortfolioStoreId(storeId));
      dispatch(setPortfolioOptions(updatedOptions));
    });
  };

  const loadPortfolioData = (): void => {
    if (isPortfolioResetPaginator) {
      setPageSize(10);
    }

    const currentDealTypeRequest = getPortfolioRequestResource(
      sort,
      searchText,
      pageSize,
      getCurrentPageForApi(),
      dealType,
      previousDealType,
      setSearchText,
    );
    dispatch(setPortfolioRequestResource(currentDealTypeRequest, dealType));
    dispatch(setIsPortfolioResetPaginator(false));
    if (!fetchingScrollData) {
      dispatch(setIsPortfolioLoading(true));
    }

    PortfolioService.fetchPortfolioData(currentDealTypeRequest)
      .then((result) => {
        if (!mounted) {
          return;
        }
        const filters = result as PortfolioFilterResource;
        if (filters?.shopperActivity) {
          filters.shopperActivity = [{ key: 'None', label: 'None' }, ...filters.shopperActivity];
          filters.shopperActivity = filters?.shopperActivity?.map((item) => {
            const convertStr = (str) => {
              // Handle special case for "None"
              if (str === 'None') {
                return 'None';
              }

              // Insert space before capital letters and capitalize first letter
              const formatted = str.replace(/([A-Z])/g, ' $1').trim();
              return formatted.charAt(0).toUpperCase() + formatted.slice(1);
            };
            return { ...item, label: convertStr(item.label) };
          });
        }
        filters.currentVehicleMakes.forEach((item) => {
          item.key = item.brandId.toString();
          item.label = BrandsService.getByBrandId(item.brandId);
        });
        filters.replacementVehicleMakes.forEach((item) => {
          item.key = item.brandId.toString();
          item.label = BrandsService.getByBrandId(item.brandId);
        });
        filters.recall.forEach((item) => {
          item.date = formatDate(item.date);
        });
        filters.insights = createInsightsFilter(
          dealType,
          currentDealTypeRequest?.filteredDealTypes,
        );
        if (result.totalCount === 0) {
          setIsNoResult(true);
          dispatch(setPortfolioDataTotalCount(result.totalCount, dealType));
          setPortfolioData([]);
        } else {
          const data =
            result.portfolioResource && fetchingScrollData
              ? portfolioData.concat(result.portfolioResource)
              : result.portfolioResource;

          data && setPortfolioData(data);
          setFetchingScrollData(false);

          dispatch(setPortfolioDataTotalCount(result.totalCount, dealType));
          setIsNoResult(false);
        }
        const notesMap: NotesCountMap[] = result.portfolioResource.map((item) => {
          return { count: item.noteCount, leadId: item.closedDealId };
        });
        if (initialRender.current) {
          initialRender.current = false;
          dispatch(setPortfolioDataTotalCount(result.totalCount, dealType));
        } else if (shouldRefreshCount > 0 || currentView.shouldRefresh) {
          dispatch(setPortfolioDataTotalCount(currentView.totalRecordsCount - 1, dealType));
        }

        setFilteredRecordsCount(result.totalCount);
        dispatch(setIsPortfolioFiltersChanged(false, dealType));
        dispatch(setIsPortfolioFilterReset(false));
        updateWithMasterFilter(filters, dealType);
        dispatch(setPortfolioShouldRefresh(false, dealType));
        dispatch(setPortfolioShouldRefreshDealCount(false, dealType));
        setShouldRefreshCount(0);
        dispatch(setPortfolioNotesCountMap(notesMap, dealType));
      })
      .catch((err) => {
        console.log(err);
        setIsTryAgain(true);
      })
      .finally(() => {
        dispatch(setIsPortfolioLoading(false));
      });
  };

  const searchCallback = (searchString: string): void => {
    setSearchText(searchString);
    setCurrentPage(1);
  };

  const getCurrentPageForApi = (): number => {
    if (currentView.isFilterChanged) {
      return 0;
    }
    if ((previousSearchText === '' && searchText === '') || previousSearchText === searchText) {
      return currentPage - 1;
    } else if (previousSearchText !== searchText || searchText === '') {
      return 0;
    }
  };

  const getTotalCount = (dealType: string): number => {
    if (dealType === 'all') {
      return portfolioOptions?.find((x) => x.label === DealType.AllDeals)?.count;
    } else {
      return portfolioOptions?.find((x) => x.key.toLowerCase() === dealType.toLowerCase())?.count;
    }
  };

  const onRowClick = (rowData: any): void => {
    if (openFilterCount > 0) {
      return;
    }

    const paginationData = portfolioData.map((x) => {
      return {
        bps: x.replacementDeal.bps_Value.toString(),
        vin: x.replacementDeal.vin,
        name: x.customer.fullName,
        dealerId: x.dealerId,
        dealType: getDealTypeLabel(x.closedDeal.dealType).toLowerCase(),
      };
    });
    dispatch(setDealsheetPagination(paginationData));
    dispatch(resetAllAiMessages());
    dispatch(setSelectedVehicleId(undefined));
    const row = rowData.original as PortfolioResource;
    openDealSheet(
      row.replacementDeal.vin,
      row.dealerId,
      getDealTypeLabel(row.closedDeal.dealType).toLowerCase(),
    );
  };

  const openPortfolioNotesModal = (value: boolean) => {
    dispatch(setPortfolioNotesModalOpen(value));
  };

  const hasDealsAssigned = (): boolean => {
    const count = getTotalCount('all');
    return count !== 0;
  };

  const noResultFoundComponent: JSX.Element = (
    <PortfolioNoResultFound
      reset={reset}
      hasAllDealsPermission={hasRole(permissions, 'allDeals')}
      hasDealsAssigned={hasDealsAssigned()}
    />
  );

  const handleScroll = (): void => {
    const { scrollHeight, scrollTop, clientHeight } = tableRef.current;
    //When we are at last page (no more records to fetch), then, don't scroll further.
    if (currentPage >= Math.ceil(currentView.totalRecordsCount / 50)) {
      return;
    }

    //clientHeight: Component height which renders table rows
    if (scrollHeight < (scrollTop + clientHeight) * 2 && !fetchingScrollData) {
      setFetchingScrollData(true);
      setCurrentPage(currentPage + 1);
    }
  };

  const canSelectRows = (): boolean => {
    return (
      hasRole(permissions, 'changeAssignee') ||
      hasRole(permissions, 'changeDealStatus') ||
      hasRole(permissions, 'markMailers') ||
      hasRole(permissions, 'userCanSaveReplacementChromeId') ||
      hasRole(permissions, 'pushLeadsInABulk') ||
      hasRole(permissions, 'reportingDataExport')
    );
  };

  if (
    isPortfolioFilterReset &&
    isNoResult &&
    currentView.totalRecordsCount !== 0 &&
    getTotalCount(dealType) !== 0
  ) {
    setIsNoResult(false);
  }

  if (
    isPortfolioFilterReset &&
    isTryAgain &&
    currentView.totalRecordsCount !== 0 &&
    getTotalCount(dealType) !== 0
  ) {
    setIsTryAgain(false);
  }

  useEffect(() => {
    mounted.current = true;
    if (isLoading && !currentView.shouldRefresh) {
      return;
    }

    if (
      currentView.shouldRefresh &&
      portfolioData.length === 1 &&
      currentPage === totalPages &&
      currentPage > 1
    ) {
      setCurrentPage(currentPage - 1);
      return;
    }

    loadPortfolioData();

    return () => {
      mounted.current = false;
    };
  }, [
    pageSize,
    currentPage,
    searchText,
    sort.accessor,
    sort.sortDirection,
    currentView.shouldRefresh,
  ]);

  // Setting the grid data from the API call when filters change.
  useEffect(() => {
    mounted.current = true;
    if (isLoading || !currentView.isFilterChanged) {
      return;
    }
    setCurrentPage(1);
    loadPortfolioData();
    return () => {
      mounted.current = false;
    };
  }, [currentView.selectedFilters]);

  useEffect(() => {
    if (storeId !== currentStoreId || currentView.shouldRefreshDealCount) {
      loadDealCounts();
    }
  }, [storeId, currentView.shouldRefreshDealCount]);

  const handleSearchBox = (isOpen) => {
    setSearchBoxOpen(isOpen);
  };

  return (
    <>
      <div data-testid='portfolio-header'>
        <PortfolioHeaderContainer className='portfolio-header-container' isMobile={isMobileDevice}>
          {!isMobileDevice && (
            <div className='portfolio-header-dropdown'>
              <PortfolioDropdown
                options={portfolioOptions}
                dealType={dealType}
                isBulkOperation={isBulkOperation}
                onPortfolioSelection={onPortfolioSelection}
              />
            </div>
          )}
          <div className='result-box-container'>
            <PortfolioSecondaryHeaderLeftContainer
              data-testid='sp-results'
              isMobileDevice={isMobileDevice}
            >
              {(!searchBoxOpen || !isMobileDevice) && (
                <PortfolioHeaderResultsContainer
                  data-testid='sp-header-results-container'
                  isTablet={isTabletDevice}
                  isMobileDevice={isMobileDevice}
                >
                  {!isNoResult && !isTryAgain && (
                    <div className='result-text-box'>
                      <Typography
                        size={theme.typography.h4}
                        fontWeight={theme.typography.fontWeights.bold}
                        color={theme.palette.dustyGray}
                      >
                        {formatStringAsPerLocale(filteredRecordsCount?.toString())} Results
                      </Typography>
                      &nbsp;
                      <Typography size={theme.typography.h4} color={theme.palette.dustyGray}>
                        (filtered from{' '}
                        {formatStringAsPerLocale(getTotalCount(dealType)?.toString())})
                      </Typography>
                    </div>
                  )}
                </PortfolioHeaderResultsContainer>
              )}
              {(searchText || !isNoResult) && !isTryAgain && (
                <PortfolioSearchContainer
                  data-testid='portfolio-searchContainer'
                  isMobileDevice={isMobileDevice}
                  isSearchBarOpen={searchBoxOpen}
                >
                  <SearchBox
                    placeholder={'Search portfolio... '}
                    searchCallback={searchCallback}
                    inputText={searchText}
                    isOpen={searchBoxOpen}
                    showCloseIcon={isMobileDevice}
                    searchIconCallback={handleSearchBox}
                  />
                </PortfolioSearchContainer>
              )}
            </PortfolioSecondaryHeaderLeftContainer>
          </div>
          {!isMobileDevice && !isNoResult && !isTryAgain && (
            <div className='portfolio-pagination-container'>
              <PaginationContainer
                pageSize={pageSize}
                count={filteredRecordsCount}
                currentPage={currentPage}
                endIndex={endIndex}
                startIndex={startIndex}
                totalPages={totalPages}
                totalCount={currentView.totalRecordsCount}
                isHeader={true}
                textValue='Showing'
                textFont={theme.typography.h4}
                textFontWeight={theme.typography.fontWeights.normal}
                setCurrentPage={setCurrentPage}
                setPageSize={setPageSize}
              />
            </div>
          )}
        </PortfolioHeaderContainer>
      </div>
      {!isTryAgain && (
        <PortfolioTable
          isMobileDevice={isMobileDevice}
          ref={tableRef}
          data-testid='portfolio-table-section'
          onScroll={handleScroll}
        >
          <ReactTableWrapper
            gridColumns={getPortFolioColumns(dealType)}
            isDynamicColumns={true}
            data={portfolioData}
            refreshCount={refreshCount}
            toggleAllRowsValue={toggleAllRowsValue}
            canSelectRows={!isMobileDevice && canSelectRows()}
            sort={sort}
            currentSortedColumn={currentSortedColumn}
            sortingClassName={'sorted-column'}
            setCurrentSortedColumn={setCurrentSortedColumn}
            sortByCallBack={onSortChange}
            setIsAllRowsSelected={setIsAllRowsSelected}
            setSelectedRows={setSelectedRows}
            onRowClick={onRowClick}
            noResultFoundBody={isNoResult ? noResultFoundComponent : <></>}
          />
          {isMobileDevice && fetchingScrollData && <FooterLoader />}
        </PortfolioTable>
      )}
      {isTryAgain && <PortfolioTryAgain retry={tryAgain}></PortfolioTryAgain>}
      {!isNoResult && !isTryAgain && !isMobileDevice && (
        <div className='row-fluid pagination-footer' data-testid='footer-section'>
          <PaginationContainer
            pageSize={pageSize}
            count={filteredRecordsCount}
            currentPage={currentPage}
            endIndex={endIndex}
            startIndex={startIndex}
            totalPages={totalPages}
            totalCount={currentView.totalRecordsCount}
            isHeader={false}
            textValue='Showing'
            textFont={theme.typography.h4}
            textFontWeight={theme.typography.fontWeights.normal}
            setCurrentPage={setCurrentPage}
            setPageSize={setPageSize}
          />
        </div>
      )}
      <PortfolioNotesModal
        dealType={dealType as DealTypeExtended}
        dealerId={notesModalResource?.dealerId}
        leadId={notesModalResource?.closedDealId}
        fullName={notesModalResource?.fullName}
        isNotesModalOpen={isNotesModalOpen}
        notesCountMap={currentView?.notesCountMap}
        status={notesModalResource?.status}
        subStatus={notesModalResource?.subStatus}
        vin={notesModalResource?.vin}
        openNotesModal={openPortfolioNotesModal}
      />
      {isPrivateIncentiveModalOpen && privateIncentiveModalResource && (
        <PrivateIncentiveModal
          isModalOpen={isPrivateIncentiveModalOpen}
          openModal={undefined}
          lastName={privateIncentiveModalResource.lastName}
          vin={privateIncentiveModalResource.vin}
          isLeaseBuyOut={privateIncentiveModalResource.isLeaseBuyOut}
          privateIncentiveLookupActionType={
            privateIncentiveModalResource.privateIncentiveLookupActionType
          }
        />
      )}
    </>
  );
};

export default PortfolioTableSection;

export const PortfolioTableWrapper = styled.div`
  background-color: white;
`;

type PortfolioHeaderContainerProps = {
  isMobile: boolean;
};

const PortfolioHeaderContainer = styled.div<PortfolioHeaderContainerProps>`
  ${(props) =>
    props.isMobile
      ? `background-color: white;
          height: 55px;`
      : ''}
`;
